import { useEffect, useState } from 'react';
import { supabase, isPlaceholderSupabase } from '../supabase';
import type { LoadState } from '../loadState';

export interface TeamVideoTag {
  id: string;
  clip_url: string;
  platform: string | null;
  notes: string | null;
  ts_in: number | null;
  ts_out: number | null;
  corner_id: string | null;
  player_id: string | null;
  player_name: string | null;
  created_at: string;
  is_highlight: boolean;
}

interface Result {
  rows: TeamVideoTag[];
  state: LoadState;
  errorMessage?: string;
}

/**
 * All video tags for the team's active roster, joined to player names.
 * Two-step: load roster → in-clause on video_tags.player_id. Same
 * shape pattern as useTeamRadar to dodge PostgREST relationship-embed
 * RLS quirks the schema-drift fix sorted out earlier.
 */
export function useTeamVideoTags(teamId: string | null | undefined): Result {
  const [rows, setRows] = useState<TeamVideoTag[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    if (!teamId) {
      setRows([]);
      setState('empty');
      return;
    }
    if (isPlaceholderSupabase()) {
      setRows([]);
      setState('empty');
      return;
    }
    let cancelled = false;
    setState('loading');
    setErrorMessage(undefined);
    void (async () => {
      // 1. Pull roster + player names so we can both filter video_tags
      //    AND map player_id → name in one pass.
      const { data: roster, error: rosterErr } = await supabase
        .from('team_players')
        .select('player_id, users!inner(name)')
        .eq('team_id', teamId)
        .eq('is_active', true);
      if (cancelled) return;
      if (rosterErr) {
        setErrorMessage(rosterErr.message);
        setState('error');
        return;
      }
      const playerMap = new Map<string, string>();
      for (const r of roster ?? []) {
        const u = (r as { users?: { name?: string | null } | null }).users;
        playerMap.set((r as { player_id: string }).player_id, u?.name ?? '');
      }
      const playerIds = Array.from(playerMap.keys());
      if (playerIds.length === 0) {
        setRows([]);
        setState('empty');
        return;
      }
      const { data: tags, error: tagErr } = await supabase
        .from('video_tags')
        .select('id, clip_url, platform, notes, ts_in, ts_out, corner_id, player_id, created_at, is_highlight')
        .in('player_id', playerIds)
        .order('created_at', { ascending: false })
        .limit(80);
      if (cancelled) return;
      if (tagErr) {
        setErrorMessage(tagErr.message);
        setState('error');
        return;
      }
      const list: TeamVideoTag[] = (tags ?? []).map((t: Record<string, unknown>) => ({
        id: t.id as string,
        clip_url: t.clip_url as string,
        platform: (t.platform as string | null) ?? null,
        notes: (t.notes as string | null) ?? null,
        ts_in: (t.ts_in as number | null) ?? null,
        ts_out: (t.ts_out as number | null) ?? null,
        corner_id: (t.corner_id as string | null) ?? null,
        player_id: (t.player_id as string | null) ?? null,
        player_name: playerMap.get((t.player_id as string) ?? '') ?? null,
        created_at: (t.created_at as string) ?? '',
        is_highlight: Boolean(t.is_highlight),
      }));
      setRows(list);
      setState(list.length > 0 ? 'ok' : 'empty');
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  return { rows, state, errorMessage };
}
